import { useEffect, useState } from 'react'

import type { Failure } from '../api/request'
import { Button } from './Control'
import { Icon } from './Icon'
import { useAnnounce } from './Announce'

/**
 * Waiting, done and failed — the same three, everywhere.
 *
 * Every area reports a long-running thing through these, so that the same situation never looks
 * different in two places. None of the three is a dialog: nothing that merely reports a state may
 * stand in the way of what somebody was doing.
 */

/**
 * How long nothing at all is shown.
 *
 * An answer that arrives in under a quarter of a second feels immediate; putting "loading" in
 * front of it and taking it away again makes the same answer feel slower than it is. Past this,
 * something is genuinely taking a while and silence would be worse.
 */
export const WAITING_GRACE_MS = 300

export interface WaitingProps {
  /** What is being waited for, as a sentence. Read out once it appears. */
  readonly label: string
  /** Skips the grace period, for a place that is known to be slow. */
  readonly immediate?: boolean
}

export function Waiting({ label, immediate = false }: WaitingProps) {
  const [shown, setShown] = useState(immediate)

  useEffect(() => {
    if (immediate) {
      return
    }
    const timer = setTimeout(() => {
      setShown(true)
    }, WAITING_GRACE_MS)
    return () => {
      clearTimeout(timer)
    }
  }, [immediate])

  if (!shown) {
    // Still marked busy, so a screen reader knows the region is not simply empty.
    return <div className="waiting" aria-busy="true" />
  }

  return (
    <div className="waiting" role="status" aria-busy="true">
      <span className="waiting__mark" aria-hidden="true" />
      <span>{label}</span>
    </div>
  )
}

/** How long a confirmation stays before it takes itself away. */
export const DONE_LINGER_MS = 2400

export interface DoneProps {
  /** What just happened, in as few words as it takes. "Saved", "Added to the desk". */
  readonly label: string
}

/**
 * A confirmation, next to the thing it is about.
 *
 * Ordinary work saves itself, so there is nothing to acknowledge and nothing to dismiss — this
 * appears where the object is and goes again on its own.
 *
 * The clock starts when it is mounted. A second confirmation for the same object should therefore
 * carry a new `key`, which is how it is told apart from the first.
 */
export function Done({ label }: DoneProps) {
  const [gone, setGone] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => {
      setGone(true)
    }, DONE_LINGER_MS)
    return () => {
      clearTimeout(timer)
    }
  }, [])

  if (gone) {
    return null
  }

  return (
    <span className="done" role="status">
      <Icon name="check" small />
      {label}
    </span>
  )
}

export interface FailedProps {
  readonly failure: Failure
  /** Offered as "Try again" when the failure is one that trying again could fix. */
  readonly onRetry?: () => void
  /** A heading, when the failure needs one. Without it this is a paragraph, not an announcement. */
  readonly title?: string
  /** Something the reader can do about it, in the frontend's own words. */
  readonly hint?: string
}

export function Failed({ failure, onRetry, title, hint }: FailedProps) {
  const announce = useAnnounce()

  return (
    <div className="notice notice--bad" role="alert">
      {title !== undefined && <h2 className="notice__title">{title}</h2>}
      <p className="notice__text">{failure.message}</p>
      {hint !== undefined && <p className="notice__text">{hint}</p>}
      {failure.detail !== null && <p className="notice__detail">{failure.detail}</p>}
      {onRetry !== undefined && failure.retryable && (
        <div className="controls controls--tight">
          <Button
            tone="quiet"
            onClick={() => {
              announce('Trying again.')
              onRetry()
            }}
          >
            Try again
          </Button>
        </div>
      )}
    </div>
  )
}
